import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { authActions } from '../../../../store/auth/slice';
import styles from './FirstStep.module.scss';
import { AxiosError } from 'axios';
import { Alert, Button, Input, Text } from 'components';
import { appActions } from 'store/app/slice';
import { usersActions } from 'store/users/slice';
import { useDispatch } from 'store/utils';

const schema = yup.object({
  email: yup.string().email('The text is not an email').required('The field is empty'),
});

type Props = {
  goToNextStep(): void;
  setUserEmail(email: string): void;
};

export const FirstStep = ({ goToNextStep, setUserEmail }: Props) => {
  const [nonExistingEmail, setNonExistingEmail] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm({ resolver: yupResolver(schema) });

  const { email: watchedEmail } = watch();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const resetPassword = async () => {
    try {
      if (!watchedEmail) return;
      dispatch(appActions.setAppIsLoading(true));
      await dispatch(usersActions.checkUserEmail(watchedEmail)).unwrap();
      setNonExistingEmail(false);
      await dispatch(authActions.resetPassword(watchedEmail)).unwrap();
      goToNextStep();
      setUserEmail(watchedEmail);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          setNonExistingEmail(true);
          return;
        }
        setError('email', { type: '400', message: error?.message });
      }
    } finally {
      dispatch(appActions.setAppIsLoading(false));
    }
  };

  const onSubmit = handleSubmit(resetPassword);

  const navigateToLogin = () => {
    navigate('/login');
  };

  const { onChange } = register('email');

  return (
    <form className={styles.formContainer}>
      <div className={styles.formContent}>
        <Input
          inputName="email"
          labelText="Email"
          placeholder="Enter your email"
          errorMsg={errors.email?.message}
          register={register}
          registerOptions={{ required: true }}
          onChange={(e) => {
            setNonExistingEmail(false);
            onChange(e);
          }}
        />
        {nonExistingEmail && (
          <Alert type="error">
            <Text type="body2">The e-mail is not registered in the system</Text>
          </Alert>
        )}
      </div>
      <div className={styles.buttonGroup}>
        <Button className={styles.button} onClick={navigateToLogin} isLink>
          Back to Log in
        </Button>
        <Button onClick={onSubmit} className={styles.button}>
          Reset password
        </Button>
      </div>
    </form>
  );
};
